import React, { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import useNow from '@hooks/use-now.ts';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  InputAdornment,
  OutlinedInput,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ArrowDownIcon,
  InputSearchIcon
} from '@shared/components/icons/svg-icon.tsx';
import { ICONS } from '@components/icons/icon.tsx';
import Loader from '@components/loader/loader.tsx';
import { CallStateEnum } from '@shared/models/call-state.model';
import AppDialog from '@shared/components/app-dialog/app-dialog.tsx';
import useGroupSettingsStore from '@modules/digital-signage/components/group-settings/store/group-settings.store.ts';
import { DeviceGroupScheduleModel } from '@models/digital-signage/device-group-schedules.model.ts';
import {
  PlaylistScheduleProps,
  ScheduleStatusEnum
} from '@modules/digital-signage/model/playlist-schedule.model.ts';
import { getSavedSlotKeys } from '@modules/digital-signage/utils/schedule-slots.utils.ts';
import { getScheduleColor } from '@modules/digital-signage/utils/schedule-appearance.utils.ts';
import { getScheduleDates } from '@modules/digital-signage/utils/schedule-date.utils.ts';
import {
  getScheduleStatus,
  sortSchedulesByStatus
} from '@modules/digital-signage/utils/schedule-status.utils.ts';
import ScheduleCard from '@modules/digital-signage/components/group-settings/components/playlist-schedule/components/schedule-card/schedule-card.tsx';
import ScheduleDialogForm from '@modules/digital-signage/components/group-settings/components/playlist-schedule/components/schedule-dialog/schedule-dialog-form.tsx';
import AccordionContentTransition from '@modules/digital-signage/components/group-settings/components/playlist-schedule/components/accordion-content-transition/accordion-content-transition.tsx';

const PlaylistSchedule: React.FC<PlaylistScheduleProps> = ({ groupId }) => {
  const { t } = useTranslation();
  const {
    schedules,
    initSchedules,
    saveLocalSchedule,
    removeLocalSchedule,
    resetSchedules,
    groupDetails,
    groupDetailsCallState
  } = useGroupSettingsStore();

  useEffect(() => {
    if (groupId && groupDetails?._id === groupId) {
      initSchedules(groupDetails.playlistSchedules ?? []);
    }
  }, [groupDetails, groupId]);

  useEffect(() => resetSchedules, []);

  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [query, setQuery] = useState<string>('');
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] =
    useState<DeviceGroupScheduleModel | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const isListLoading =
    !!groupId &&
    !schedules.length &&
    [CallStateEnum.Init, CallStateEnum.Loading].includes(groupDetailsCallState);

  const nowMs = useNow(60000);
  const savedSchedules = useMemo(
    () =>
      new Map(
        (groupId && groupDetails?._id === groupId
          ? (groupDetails.playlistSchedules ?? [])
          : []
        ).map((schedule) => [schedule._id, schedule])
      ),
    [groupDetails, groupId]
  );
  const savedSlotKeys = useMemo(
    () =>
      new Map(
        schedules.map((schedule) => [
          schedule._id,
          getSavedSlotKeys(
            savedSchedules.get(schedule._id),
            getScheduleDates(schedule)
          )
        ])
      ),
    [schedules, savedSchedules]
  );
  const getSavedKeys = (schedule: DeviceGroupScheduleModel) =>
    savedSlotKeys.get(schedule._id) ?? new Set<string>();

  const scheduleItems = useMemo(
    () =>
      sortSchedulesByStatus(
        schedules.map((schedule, index) => ({
          schedule,
          index,
          color: getScheduleColor(index),
          status: getScheduleStatus(
            schedule,
            dayjs(nowMs),
            savedSlotKeys.get(schedule._id) ?? new Set<string>()
          )
        }))
      ),
    [schedules, savedSlotKeys, nowMs]
  );

  const filteredSchedules = useMemo(() => {
    const search = query.trim().toLowerCase();
    return scheduleItems.filter(({ schedule }) =>
      schedule.name.toLowerCase().includes(search)
    );
  }, [scheduleItems, query]);

  const activeCount = scheduleItems.filter(({ status }) =>
    [ScheduleStatusEnum.Active, ScheduleStatusEnum.Live].includes(status)
  ).length;

  const handleOpenDialog = (schedule: DeviceGroupScheduleModel | null) => {
    setSelectedItem(schedule);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  return (
    <>
      <Accordion
        className="bordered-accordion flex! flex-col min-h-0 overflow-hidden"
        slots={{ transition: AccordionContentTransition }}
        slotProps={{
          region: {
            className: `grid min-h-0 transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
              isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            }`
          }
        }}
        disableGutters
        elevation={0}
        square
        expanded={isExpanded}
        onChange={(_, expanded) => setIsExpanded(expanded)}
      >
        <AccordionSummary
          expandIcon={<ArrowDownIcon />}
          aria-controls="playlist-schedule-content"
          id="playlist-schedule-header"
        >
          <div className="flex flex-col gap-0.5">
            <Typography
              variant="subtitle4"
              className="text-(--dark-1)! max-lg-xl:text-[16px]!"
            >
              {t('digital_signage_playlist_schedule')}
            </Typography>
            <Typography variant="caption" className="text-(--gray-2)!">
              {t('digital_signage_schedule_summary', {
                count: schedules.length,
                active: activeCount
              })}
            </Typography>
          </div>
        </AccordionSummary>

        <div className="flex flex-col min-h-0 overflow-hidden">
          <AccordionDetails
            inert={!isExpanded}
            className="flex flex-col min-h-0"
          >
            <div className="flex flex-col gap-4 min-h-0">
              <div className="flex items-center gap-2">
                <Button
                  variant="contained"
                  startIcon={ICONS.plusIcon}
                  onClick={() => handleOpenDialog(null)}
                  className="h-8.5 min-w-25! px-3! shrink-0"
                >
                  <Typography
                    variant="caption"
                    component="span"
                    className="text-xs! text-inherit! font-[inherit]!"
                  >
                    {t('digital_signage_schedule_button')}
                  </Typography>
                </Button>
                <OutlinedInput
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t('placeholder_digital_signage_schedule_search')}
                  className="flex-1 min-w-0"
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderRadius: '30px'
                    },
                    '& .MuiInputBase-input': {
                      padding: '8px 10px',
                      fontSize: '14px'
                    }
                  }}
                  startAdornment={
                    <InputAdornment position="start">
                      <InputSearchIcon
                        fill="var(--gray-2)"
                        className="w-4! h-4! max-lg-xl:w-3.5! max-lg-xl:h-3.5!"
                      />
                    </InputAdornment>
                  }
                />
              </div>

              <div
                ref={listRef}
                className="flex flex-col gap-2.5 min-h-0 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {filteredSchedules.map(({ schedule, color }) => (
                  <ScheduleCard
                    key={schedule._id}
                    schedule={schedule}
                    color={color}
                    savedSlotKeys={getSavedKeys(schedule)}
                    onEdit={handleOpenDialog}
                    onDelete={(item) => removeLocalSchedule(item._id)}
                  />
                ))}

                {isListLoading && (
                  <div className="flex items-center justify-center py-10">
                    <Loader />
                  </div>
                )}

                {!isListLoading && !filteredSchedules.length && (
                  <div className="flex flex-col items-center justify-center gap-1 px-4 py-6 text-center">
                    <Typography
                      variant="body2"
                      className="font-semibold! text-(--gray-1)!"
                    >
                      {t(
                        schedules.length
                          ? 'digital_signage_schedule_empty_search_title'
                          : 'digital_signage_schedule_empty_title'
                      )}
                    </Typography>
                    <Typography variant="caption" className="text-(--gray-2)!">
                      {t(
                        schedules.length
                          ? 'digital_signage_schedule_empty_description'
                          : 'digital_signage_schedule_empty_hint'
                      )}
                    </Typography>
                  </div>
                )}
              </div>
            </div>
          </AccordionDetails>
        </div>
      </Accordion>

      <AppDialog
        open={openDialog}
        title={t(
          selectedItem
            ? 'digital_signage_edit_schedule_title'
            : 'digital_signage_add_schedule_title'
        )}
        width={1120}
        fullHeight
        content={
          <ScheduleDialogForm
            selectedItem={selectedItem}
            onCancel={handleCloseDialog}
            onSave={(body) => {
              saveLocalSchedule({ id: selectedItem?._id, body });
              if (!selectedItem) {
                listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
              }
              handleCloseDialog();
            }}
          />
        }
        onClose={handleCloseDialog}
      />
    </>
  );
};

export default PlaylistSchedule;
